import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, API } from "@/App";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const RANK_COLORS = ["bg-amber-100 text-amber-700", "bg-slate-200 text-slate-600", "bg-orange-100 text-orange-700"];
import { LogOut, Plus, Play, ChevronRight, Trophy, Hospital, Users, Eye, Activity, TrendingUp, Clock, ArrowUpRight, Beaker, ChevronDown, FileDown } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import ScenarioBuilder from "@/components/ScenarioBuilder";
import AdvancedAnalytics from "@/components/AdvancedAnalytics";

export default function InstructorDashboard() {
  const { logout, user } = useAuth();
  const { t } = useTranslation();
  const { simId } = useParams();
  const navigate = useNavigate();
  const [simulations, setSimulations] = useState([]);
  const [selectedSim, setSelectedSim] = useState(null);
  const [hospitals, setHospitals] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [hospitalName, setHospitalName] = useState("");
  const [showScenario, setShowScenario] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const prevSubmittedRef = useRef(new Set());

  useEffect(() => {
    loadSimulations();
  }, []);

  const loadSimulations = async () => {
    try {
      const res = await axios.get(`${API}/simulations`);
      setSimulations(res.data);
      if (simId) {
        const found = res.data.find(s => s.id === simId);
        if (found) setSelectedSim(found);
      } else if (res.data.length > 0 && !selectedSim) {
        setSelectedSim(res.data[0]);
      }
    } catch {
      toast.error("Error loading simulations");
    }
  };

  const loadSimDetail = useCallback(async () => {
    if (!selectedSim) return;
    try {
      const [hRes, lbRes] = await Promise.all([
        axios.get(`${API}/simulations/${selectedSim.id}/hospitals`),
        axios.get(`${API}/simulations/${selectedSim.id}/leaderboard`),
      ]);
      const newHospitals = hRes.data;

      // Detect new submissions and notify
      const nowSubmitted = new Set(newHospitals.filter(h => h.decisions_submitted).map(h => h.id));
      const prev = prevSubmittedRef.current;
      if (prev.size > 0) {
        for (const h of newHospitals) {
          if (h.decisions_submitted && !prev.has(h.id)) {
            toast.success(`${h.name} has submitted decisions`, { duration: 6000 });
          }
        }
      }
      prevSubmittedRef.current = nowSubmitted;

      setHospitals(newHospitals);
      setLeaderboard(lbRes.data);
    } catch {
      toast.error("Error loading hospitals");
    }
  }, [selectedSim]);

  useEffect(() => { loadSimDetail(); }, [loadSimDetail]);

  // Auto-poll every 15s for submission updates
  useEffect(() => {
    if (!selectedSim || selectedSim.status !== "active") return;
    const interval = setInterval(loadSimDetail, 15000);
    return () => clearInterval(interval);
  }, [selectedSim, loadSimDetail]);

  const extractError = (e) => {
    const detail = e.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join(", ");
    return "Error";
  };

  const advanceRound = async () => {
    try {
      const res = await axios.post(`${API}/simulations/${selectedSim.id}/advance`);
      toast.success(`${t("instructor.advanceRound")}: ${res.data.new_round}`);
      loadSimulations();
      loadSimDetail();
    } catch (e) { toast.error(extractError(e)); }
  };

  const startSimulation = async () => {
    try {
      await axios.put(`${API}/simulations/${selectedSim.id}/start`);
      toast.success(t("instructor.startSimulation"));
      loadSimulations();
      loadSimDetail();
    } catch (e) { toast.error(extractError(e)); }
  };

  const createHospital = async () => {
    if (!hospitalName.trim()) return;
    try {
      await axios.post(`${API}/simulations/${selectedSim.id}/hospitals`, { name: hospitalName });
      setHospitalName("");
      setShowCreateDialog(false);
      toast.success(t("instructor.createHospital"));
      loadSimDetail();
    } catch (e) { toast.error(extractError(e)); }
  };

  const submitted = hospitals.filter(h => h.decisions_submitted).length;
  const pending = hospitals.length - submitted;
  const avgProfit = hospitals.length > 0 ? Math.round(hospitals.reduce((s, h) => s + (h.cumulative_profit || 0), 0) / hospitals.length) : 0;

  const downloadFinalReport = async () => {
    try {
      const res = await axios.get(`${API}/simulations/${selectedSim.id}/final-report`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${selectedSim.name.replace(/ /g, "_")}_Final_Report.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error generating report"); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="instructor-dashboard">
      {/* HEADER */}
      <header className="bg-white/90 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-medsim.png" alt="MedSim" className="h-10 rounded-md" />
            <span className="text-lg font-heading font-semibold text-slate-900 tracking-tight">MedSim</span>
            <span className="text-xs text-slate-400 font-medium">/</span>
            <span className="text-sm text-slate-500">{t("instructor.title")}</span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={async () => {
              try {
                const res = await axios.get(`${API}/instructor-guide-pdf`, { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement("a"); a.href = url;
                a.download = "MedBoard_Instructor_Guide_ENGESP.pdf";
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
              } catch { toast.error("Error downloading guide"); }
            }} data-testid="download-instructor-guide-btn">
              <FileDown className="w-4 h-4 mr-1.5" />Instructor
            </Button>
            <Button variant="outline" size="sm" onClick={async () => {
              try {
                const res = await axios.get(`${API}/student-guide-pdf`, { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement("a"); a.href = url;
                a.download = "MedBoard_Student_Guide_ENGESP.pdf";
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
              } catch { toast.error("Error downloading guide"); }
            }} data-testid="download-student-guide-btn">
              <FileDown className="w-4 h-4 mr-1.5" />Student
            </Button>
            <ThemeToggle />
            <LanguageSwitcher />
            <span className="text-sm text-slate-500">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* SIDEBAR — Simulation List */}
          <aside className="w-64 flex-shrink-0 space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 px-1 mb-3">{t("instructor.mySimulations")}</h3>
            {simulations.length === 0 && (
              <p className="text-sm text-slate-400 px-1">{t("instructor.noSimulations")}</p>
            )}
            {simulations.map(sim => (
              <button
                key={sim.id}
                onClick={() => setSelectedSim(sim)}
                className={`w-full text-left px-4 py-3 rounded-lg border transition-all duration-200 ${
                  selectedSim?.id === sim.id
                    ? "border-slate-900 bg-slate-900 text-white shadow-sm"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:shadow-sm"
                }`}
                data-testid={`sim-card-${sim.id}`}
              >
                <div className="font-heading font-medium text-sm truncate">{sim.name}</div>
                <div className={`text-xs mt-1 flex items-center gap-2 ${selectedSim?.id === sim.id ? "text-slate-300" : "text-slate-400"}`}>
                  <Badge variant={selectedSim?.id === sim.id ? "secondary" : "outline"} className="text-[10px] px-1.5 py-0">{sim.status}</Badge>
                  <span>{t("common.round")} {sim.current_round}/{sim.rounds_count}</span>
                </div>
              </button>
            ))}
          </aside>

          {/* MAIN CONTENT */}
          <main className="flex-1 min-w-0 space-y-6">
            {!selectedSim ? (
              <Card className="border-slate-200"><CardContent className="p-12 text-center text-slate-400">
                <Hospital className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>{t("instructor.noSimulations")}</p>
              </CardContent></Card>
            ) : (
              <>
                {/* EXECUTIVE SUMMARY BAR */}
                <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm animate-fade-in-up" data-testid="executive-summary">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">{selectedSim.name}</h1>
                      <div className="flex items-center gap-3 mt-1.5">
                        <Badge variant={selectedSim.status === "active" ? "default" : "secondary"} className="text-xs">{
                          selectedSim.status === "active" ? "Activa"
                          : (selectedSim.status === "pending" || selectedSim.status === "setup") ? "Pendiente"
                          : selectedSim.status
                        }</Badge>
                        <span className="text-sm text-slate-500">{t("instructor.currentRound")}: <strong className="text-slate-900">{selectedSim.current_round}</strong> {t("instructor.roundsTotal", { total: selectedSim.rounds_count })}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {(selectedSim.status === "pending" || selectedSim.status === "setup") && (
                        <Button onClick={startSimulation} className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm" data-testid="start-sim-btn">
                          <Play className="w-4 h-4 mr-2" />{t("instructor.startSimulation")}
                        </Button>
                      )}
                      {selectedSim.status === "active" && (
                        <Button onClick={advanceRound} className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm" data-testid="advance-round-btn">
                          <ChevronRight className="w-4 h-4 mr-1" />{t("instructor.advanceRound")}
                        </Button>
                      )}
                      {selectedSim.status === "active" && (
                        <Button variant="outline" onClick={downloadFinalReport} data-testid="final-report-btn" title="Download Final Report">
                          <FileDown className="w-4 h-4 mr-1.5" />Report
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* KPI STRIP */}
                  <div className="grid grid-cols-4 gap-4 pt-4 border-t border-slate-100">
                    <div className="animate-fade-in-up stagger-1" data-testid="kpi-hospitals">
                      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1">{t("admin.hospitals")}</p>
                      <p className="text-3xl font-heading font-light text-slate-900 tracking-tighter">{hospitals.length}</p>
                    </div>
                    <div className="animate-fade-in-up stagger-2" data-testid="kpi-submitted">
                      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1">{t("instructor.decisionsSubmitted") || "Decisiones Enviadas"}</p>
                      <p className="text-3xl font-heading font-light text-slate-900 tracking-tighter">{submitted}<span className="text-lg text-slate-400">/{hospitals.length}</span></p>
                    </div>
                    <div className="animate-fade-in-up stagger-3" data-testid="kpi-pending">
                      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1">{t("instructor.pendingDecisions") || "Pendientes"}</p>
                      <p className={`text-3xl font-heading font-light tracking-tighter ${pending > 0 ? "text-amber-600" : "text-emerald-600"}`}>{pending}</p>
                    </div>
                    <div className="animate-fade-in-up stagger-4" data-testid="kpi-avg-profit">
                      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1">{t("instructor.avgProfit") || "Beneficio Medio"}</p>
                      <p className={`text-3xl font-heading font-light tracking-tighter ${avgProfit >= 0 ? "text-emerald-600" : "text-red-500"}`}>${(avgProfit / 1000).toFixed(0)}k</p>
                    </div>
                  </div>

                  {/* NEXT STEPS CONTEXT */}
                  {selectedSim.status === "active" && (
                    <div className="mt-4 pt-4 border-t border-slate-100 space-y-2" data-testid="next-steps">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-sky-600" />
                        <p className="text-sm text-slate-600">
                          {pending > 0
                            ? (t("instructor.waitingDecisions") || `Waiting for ${pending} team decisions before advancing.`)
                            : (t("instructor.allSubmitted") || "All teams have submitted. You can advance to the next year.")}
                        </p>
                      </div>
                      {hospitals.length > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-500 bg-emerald-500" style={{ width: `${(submitted / hospitals.length) * 100}%` }} />
                          </div>
                          <span className="text-xs font-mono text-slate-500">{submitted}/{hospitals.length}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* SCENARIO BUILDER TOGGLE */}
                {selectedSim && (selectedSim.status === "setup" || selectedSim.status === "pending" || selectedSim.status === "active") && (
                  <div className="bg-white border border-slate-200 rounded-lg shadow-sm animate-fade-in-up overflow-hidden">
                    <button
                      onClick={() => setShowScenario(!showScenario)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                      data-testid="scenario-toggle-btn"
                    >
                      <div className="flex items-center gap-2">
                        <Beaker className="w-4 h-4 text-violet-500" />
                        <span className="text-sm font-heading font-medium text-slate-900">{t("scenario.title")}</span>
                        <Badge variant="outline" className="text-[10px] border-violet-200 text-violet-600">P2</Badge>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showScenario ? "rotate-180" : ""}`} />
                    </button>
                    {showScenario && (
                      <div className="px-6 pb-6 border-t border-slate-100">
                        <div className="pt-4">
                          <ScenarioBuilder simId={selectedSim.id} />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ADVANCED ANALYTICS TOGGLE */}
                {selectedSim && selectedSim.status === "active" && (
                  <div className="bg-white border border-slate-200 rounded-lg shadow-sm animate-fade-in-up overflow-hidden">
                    <button
                      onClick={() => setShowAnalytics(!showAnalytics)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                      data-testid="analytics-toggle-btn"
                    >
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm font-heading font-medium text-slate-900">{t("analytics.title")}</span>
                        <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-600">NEW</Badge>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showAnalytics ? "rotate-180" : ""}`} />
                    </button>
                    {showAnalytics && (
                      <div className="px-6 pb-6 border-t border-slate-100">
                        <div className="pt-4">
                          <AdvancedAnalytics simId={selectedSim.id} />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* HOSPITALS TABLE */}
                <div className="bg-white border border-slate-200 rounded-lg shadow-sm animate-fade-in-up" data-testid="hospitals-section">
                  <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Hospital className="w-4 h-4 text-slate-400" />
                      <h2 className="text-base font-heading font-medium text-slate-900">{t("admin.hospitals")}</h2>
                      <Badge variant="outline" className="text-xs">{hospitals.length}</Badge>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setShowCreateDialog(true)} className="border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="create-hospital-btn">
                      <Plus className="w-3.5 h-3.5 mr-1.5" />{t("instructor.createHospital")}
                    </Button>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50/50">
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.name")}</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("instructor.joinCode")}</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("instructor.members")}</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">{t("hospital.cumProfit")}</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">{t("hospital.reputation")}</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.status")}</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {hospitals.map((h, idx) => (
                        <TableRow key={h.id} className="group hover:bg-slate-50/50 transition-colors">
                          <TableCell className="font-medium text-slate-900">{h.name}</TableCell>
                          <TableCell><code className="text-xs bg-slate-100 px-2 py-0.5 rounded font-mono text-slate-600">{h.join_code}</code></TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <Users className="w-3.5 h-3.5 text-slate-400" />
                              <span className="text-sm text-slate-600">{h.members?.length || 0}</span>
                              {(!h.members || h.members.length === 0) && <Badge variant="outline" className="text-[10px] ml-1 border-sky-200 text-sky-600">IA</Badge>}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            <span className={h.cumulative_profit >= 0 ? "text-emerald-600" : "text-red-500"}>
                              ${(h.cumulative_profit || 0).toLocaleString()}
                            </span>
                          </TableCell>
                          <TableCell className="text-right text-sm text-slate-600">{h.reputation || 50}/100</TableCell>
                          <TableCell>
                            {h.decisions_submitted
                              ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">{t("instructor.submitted") || "Enviado"}</Badge>
                              : <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">{t("instructor.pending") || "Pendiente"}</Badge>
                            }
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/view/hospital/${h.id}`)} data-testid={`view-hospital-${h.id}`} title={t("common.viewHospital")}>
                              <Eye className="w-4 h-4 text-slate-400 group-hover:text-sky-600 transition-colors" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {hospitals.length === 0 && (
                        <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">
                          {t("instructor.noHospitals") || "No hay hospitales. Crea el primero para empezar."}
                        </TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* LEADERBOARD */}
                {leaderboard.length > 0 && (
                  <div className="bg-white border border-slate-200 rounded-lg shadow-sm animate-fade-in-up" data-testid="leaderboard-section">
                    <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-amber-500" />
                      <h2 className="text-base font-heading font-medium text-slate-900">{t("instructor.leaderboard")}</h2>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {leaderboard.map((entry, idx) => (
                        <div key={entry.hospital_id} className="px-6 py-3 flex items-center gap-4 hover:bg-slate-50/50 transition-colors">
                          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${RANK_COLORS[idx] || "bg-slate-100 text-slate-500"}`}>{idx + 1}</span>
                          <span className="flex-1 font-medium text-slate-900 text-sm">{entry.name}</span>
                          <div className="flex items-center gap-6 text-sm">
                            <span className="text-slate-500">{t("hospital.reputation")}: <strong className="text-slate-900">{entry.reputation}/100</strong></span>
                            <span className={`font-mono font-medium ${entry.cumulative_profit >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                              ${(entry.cumulative_profit || 0).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {/* CREATE HOSPITAL DIALOG */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{t("instructor.createHospital")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("instructor.hospitalName")}</Label>
              <Input value={hospitalName} onChange={e => setHospitalName(e.target.value)} placeholder="Hospital San Rafael" className="mt-1.5 border-slate-300" data-testid="hospital-name-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={createHospital} className="bg-slate-900 hover:bg-slate-800" data-testid="confirm-create-hospital">{t("common.create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
